//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import React, { useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import styled from 'styled-components';

import { SAMPLING_CAMPAIGNS_TRACKING_ID } from '@campaigns/constants/sampling/SamplingCampaignsTrackingId';
import { EditableBVSamplingProduct } from '@campaigns/types/sampling/SamplingProductTypes';
import { PrimaryLinkButton } from '@components/atoms/button/Button';
import { HR } from '@components/atoms/hr/HR';
import { BodyBoldText, BodyText } from '@components/atoms/text/Text';

export const VISIBLE_VARIANTS_LIMIT = 5;

interface ProductVariantsSummaryProps {
  isSubmitting?: boolean;
  isViewOnly: boolean;
  onEditVariants: () => void;
}

export const ProductVariantsSummary = ({
  isSubmitting = false,
  isViewOnly,
  onEditVariants,
}: ProductVariantsSummaryProps) => {
  const { control } = useFormContext<EditableBVSamplingProduct>();
  const productName = useWatch({ control, name: 'name' });
  const variations = useWatch({ control, name: 'variations' });
  const [isExpanded, setIsExpanded] = useState(false);

  // A single row is the product itself, not a set of variants
  if (variations.length <= 1) {
    return null;
  }

  const visibleVariations = isExpanded
    ? variations
    : variations.slice(0, VISIBLE_VARIANTS_LIMIT);
  const hiddenCount = variations.length - visibleVariations.length;

  return (
    <Container>
      <Header>
        <HeaderText>Variants</HeaderText>
        <HR />
        {!isViewOnly && (
          <PrimaryLinkButton
            isDisabled={isSubmitting}
            onClick={onEditVariants}
            trackingId={
              SAMPLING_CAMPAIGNS_TRACKING_ID.PRODUCT_DRAWER_EDIT_VARIANTS_BUTTON
            }
          >
            Edit Variants
          </PrimaryLinkButton>
        )}
      </Header>
      <VariantList>
        {visibleVariations.map((variation, index) => (
          <BodyText key={`${variation.variantName ?? 'variant'}-${index}`}>
            {[productName, variation.variantName || `Variant #${index + 1}`]
              .filter(Boolean)
              .join(', ')}
          </BodyText>
        ))}
      </VariantList>
      {hiddenCount > 0 && (
        <div>
          <PrimaryLinkButton
            onClick={() => setIsExpanded(true)}
            trackingId={
              SAMPLING_CAMPAIGNS_TRACKING_ID.PRODUCT_DRAWER_VIEW_MORE_VARIANTS_BUTTON
            }
          >
            {`View ${hiddenCount} more ${hiddenCount === 1 ? 'Variant' : 'Variants'}`}
          </PrimaryLinkButton>
        </div>
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacings[3]};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacings[3]};

  ${HR} {
    flex: 1;
  }
`;

const HeaderText = styled(BodyBoldText)`
  white-space: nowrap;
`;

const VariantList = styled.div`
  display: flex;
  flex-direction: column;
`;
